'use client'

import { Divider, InfoBanner, InputText, Spacer } from '@vinted/web-ui'
import { SyntheticEvent, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { CountryModel } from '@marketplace-web/country/country-data'
import { BusinessRegistrationModel } from '@marketplace-web/business-accounts/business-accounts-data'
import { Heading } from '@marketplace-web/business-accounts/business-accounts-ui'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { NewBusinessAddressField } from '../../../../../../constants'
import { GeneralError } from '../../../../GeneralError'
import { Section } from '../../../../Section'
import useCityOptions from '../../../useCityOptions'
import { camelCase, sanitizePostalCode } from '../../../utils'
import { InputDropdownController, InputDropdownOption } from '../InputDropdownController'
import { isValueInObject } from '../../../../../../utils/object'

const TRANSLATION_PREFIX = 'business.registration'

type Props = {
  countryOptions: Array<InputDropdownOption>
  selectedCountry: CountryModel | undefined
  defaultValues: BusinessRegistrationModel
  apiErrors: Array<ErrorItem> | null
  showGeneralError: boolean
}

const BusinessAddressInitialDetails = ({
  countryOptions,
  selectedCountry,
  apiErrors = [],
  defaultValues,
  showGeneralError,
}: Props) => {
  const translate = useTranslate(`${TRANSLATION_PREFIX}.addresses`)
  const [defaultCity, setDefaultCity] = useState<string>('')

  const {
    register,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors },
  } = useFormContext<Partial<BusinessRegistrationModel>>()

  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)
  const postalCode: string | undefined = watch(NewBusinessAddressField.PostalCode)

  const ukPostalCodeAbTest = useAbTest('b2c_postal_code_field_example_uk')
  const isUkPostalCodeAbTestExposed = selectedCountry?.isoCode === 'GB'
  const isUkPostalCodeAbTestOn = ukPostalCodeAbTest?.variant === 'on' && isUkPostalCodeAbTestExposed

  useTrackAbTest(ukPostalCodeAbTest, isUkPostalCodeAbTestExposed)

  const { cityOptions, fullPostalCode } = useCityOptions(selectedCountry, postalCode)

  useEffect(() => {
    if (cityOptions.length > 0) {
      setDefaultCity(cityOptions[0]!.value)
    }
  }, [cityOptions])

  useEffect(() => {
    setValue(NewBusinessAddressField.FullPostalCode, fullPostalCode || '')
  }, [fullPostalCode, setValue])

  useEffect(() => {
    apiErrors?.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, NewBusinessAddressField)) return

      setError(fieldName, {
        type: 'manual',
        message: value,
      })
    })
  }, [setError, apiErrors])

  useEffect(() => {
    if (!defaultValues.businessDetails || !defaultValues.addresses) return
    reset({ businessDetails: defaultValues.businessDetails, addresses: defaultValues.addresses })
  }, [reset, defaultValues])

  return (
    <SeparatedList separator={<Spacer size="x2-large" />}>
      <Heading styling="tight" title={translate('title')} subtitle={translate('subtitle')} />
      {showGeneralError && <GeneralError />}
      <InfoBanner type="info" styling="tight" body={translate('note')} />
      <Section>
        <SeparatedList separator={<Divider />}>
          <InputDropdownController
            title={translate('business_address.country_id.title')}
            options={countryOptions}
            input={NewBusinessAddressField.Country}
            translationPrefix={TRANSLATION_PREFIX}
            required
            disabled
          />
          <InputText
            {...register(NewBusinessAddressField.AddressLine1, {
              required: true,
            })}
            styling="wide"
            title={translate('business_address.line1.title')}
            placeholder={translate('business_address.line1.placeholder')}
            validation={renderValidation(validationMessage(NewBusinessAddressField.AddressLine1))}
          />
          <InputText
            {...register(NewBusinessAddressField.AddressLine2)}
            styling="wide"
            title={translate('business_address.line2.title')}
            placeholder={translate('business_address.line2.placeholder')}
            validation={renderValidation(validationMessage(NewBusinessAddressField.AddressLine2))}
          />
          <InputText
            {...register(NewBusinessAddressField.PostalCode, {
              required: true,
              onBlur: (event: SyntheticEvent<HTMLInputElement>) => {
                if (!isUkPostalCodeAbTestOn) return

                setValue(
                  NewBusinessAddressField.PostalCode,
                  sanitizePostalCode({
                    postalCode: event.currentTarget.value,
                    countryCode: selectedCountry?.isoCode,
                  }),
                )
              },
            })}
            styling="wide"
            title={translate('business_address.postal_code.title')}
            placeholder={translate(
              isUkPostalCodeAbTestOn
                ? 'business_address.postal_code.placeholder_uk'
                : 'business_address.postal_code.placeholder',
            )}
            validation={renderValidation(validationMessage(NewBusinessAddressField.PostalCode))}
          />
          <InputDropdownController
            title={translate('business_address.city.title')}
            options={cityOptions}
            input={NewBusinessAddressField.City}
            translationPrefix={TRANSLATION_PREFIX}
            defaultValue={defaultCity}
            required
          />
        </SeparatedList>
      </Section>
    </SeparatedList>
  )
}

export default BusinessAddressInitialDetails
